import { Injectable, Injector, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AUTH_CREDENTIALS } from './http-context.tokens';
import { UserService } from '../services/user.service';

/**
 * OB-7718 фаза 9: перехват 401 token_expired → single-flight refresh → retry.
 *
 * Регистрируется МЕЖДУ HttpClearParams и HttpToken: retry (повторный
 * next.handle) снова проходит через Token и берёт уже СВЕЖИЙ access-токен
 * из куки, обновлённой refresh'ем.
 *
 * На SSR refresh запрещён принципиально: ротация инвалидировала бы
 * refresh-куку браузера, а доставить новую SSR не может — 401 на сервере
 * просто отдаёт гостевой рендер.
 *
 * UserService достаётся лениво через Injector — прямая инжекция в конструктор
 * дала бы DI-цикл interceptor → UserService → HttpClient → interceptors.
 */
@Injectable()
export class HttpAuthInterceptor implements HttpInterceptor {
    private injector = inject(Injector);
    private isServer = isPlatformServer(inject(PLATFORM_ID));

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(catchError((err: any) => {
            // фаза 10: 403 = права реально изменились с момента последней выдачи
            // identity — фоновая ревалидация обновит permissions в UI (дебаунс
            // внутри revalidate); сам запрос НЕ ретраится
            if (err instanceof HttpErrorResponse && err.status === 403
                && !this.isServer && !req.context.get(AUTH_CREDENTIALS)) {
                this.injector.get(UserService).revalidate();
                return throwError(() => err);
            }
            const expired = err instanceof HttpErrorResponse && err.status === 401
                && (err.error?.code === 'token_expired' || err.headers?.get('X-Auth-Error') === 'token_expired');
            // сами auth-запросы (login/refresh/logout) не ретраим
            if (!expired || this.isServer || req.context.get(AUTH_CREDENTIALS)) {
                return throwError(() => err);
            }
            const us = this.injector.get(UserService);
            return from(us.refreshTokens()).pipe(
                switchMap((ok: boolean) => ok ? next.handle(req) : throwError(() => err)),
            );
        }));
    }
}
